import { useMemo, useState } from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { mergeTimeline } from '@pv/common/assistants';
import AdminTimeline from '../components/AssistantConversationDetails/Timeline';
import RunDiagnostics from '../components/AssistantConversationDetails/RunDiagnostics';
import type {
  AdminConversation,
  AdminEntry,
} from '../components/AssistantConversationDetails/types';
import AssistantConversationsList from './AssistantConversationsList';

const CONVERSATION_USER = gql`
  query AdminConversationUser($id: ID!) {
    user(id: $id) {
      id
      email
      firstName
      lastName
    }
  }
`;

const DETAIL = gql`
  query AdminAssistantConversation(
    $id: ID!
    $beforeSequence: Int
    $beforeRunId: ID
  ) {
    adminAssistantConversations(
      id: $id
      beforeSequence: $beforeSequence
      beforeRunId: $beforeRunId
    )
  }
`;

function ConversationDetail({ id }: { id: string }) {
  const client = useApolloClient();
  const [params] = useSearchParams();
  const back = new URLSearchParams(params);
  back.delete('id');
  const [beforeRunId, setBeforeRunId] = useState<string>();
  const [older, setOlder] = useState<{
    timeline: AdminEntry[];
    hasMore: boolean;
  } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const detail = useQuery<{ adminAssistantConversations: AdminConversation }>(
    DETAIL,
    {
      variables: { id, beforeRunId },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );
  const conversation = (detail.data || detail.previousData)
    ?.adminAssistantConversations;
  const { data: userData } = useQuery<{
    user: { email?: string; firstName?: string; lastName?: string } | null;
  }>(CONVERSATION_USER, {
    variables: { id: conversation?.userId },
    skip: !conversation?.userId,
  });
  const entries = useMemo(
    () =>
      conversation
        ? mergeTimeline(older?.timeline || [], conversation.timeline)
        : [],
    [conversation, older]
  );
  const loadEarlier = async () => {
    if (!entries.length || historyLoading) return;
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const { data } = await client.query<{
        adminAssistantConversations: AdminConversation;
      }>({
        query: DETAIL,
        variables: { id, beforeSequence: entries[0].sequence },
        fetchPolicy: 'network-only',
      });
      const previous = data.adminAssistantConversations;
      setOlder({
        hasMore: previous.hasMore,
        timeline: mergeTimeline(entries, previous.timeline),
      });
    } catch {
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  };
  const refresh = () => {
    void detail.refetch().catch(() => undefined);
  };
  const runs = conversation?.runs || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: 'auto' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Button
          component={RouterLink}
          to={`/admin/assistant-conversations${
            back.toString() ? `?${back}` : ''
          }`}
          startIcon={<ArrowBackIcon />}
        >
          Back to conversations
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refresh}
          disabled={detail.loading}
          sx={{ flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Stack>
      <Box sx={{ mb: 3, minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary">
          Assistant conversation
        </Typography>
        <Typography
          component="h1"
          variant="h6"
          sx={{ overflowWrap: 'anywhere' }}
        >
          {conversation?.title || 'Conversation details'}
        </Typography>
      </Box>
      {detail.loading && (
        <LinearProgress aria-label="Loading conversation" sx={{ mb: 2 }} />
      )}
      {detail.error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={refresh}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Unable to load conversation details.
        </Alert>
      )}
      {detail.loading && !conversation && (
        <>
          <Skeleton height={120} />
          <Skeleton height={160} />
          <Skeleton height={160} />
        </>
      )}
      {conversation && (
        <>
          {(older?.hasMore ?? conversation.hasMore) && (
            <Button
              variant="outlined"
              onClick={loadEarlier}
              disabled={historyLoading || detail.loading || !entries.length}
              sx={{ mb: 2 }}
            >
              {historyLoading
                ? 'Loading earlier history…'
                : 'Load earlier history'}
            </Button>
          )}
          {historyError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" onClick={loadEarlier}>
                  Retry history
                </Button>
              }
            >
              Unable to load earlier history.
            </Alert>
          )}
          <AdminTimeline
            entries={entries}
            userEmail={userData?.user?.email}
            userId={conversation.userId}
            userName={[userData?.user?.firstName, userData?.user?.lastName]
              .filter(Boolean)
              .join(' ')}
          />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 4, mb: 2 }}
          >
            <Typography variant="h6">Run diagnostics</Typography>
            <Stack direction="row" spacing={1}>
              {beforeRunId && (
                <Button
                  disabled={detail.loading}
                  onClick={() => setBeforeRunId(undefined)}
                >
                  Latest runs
                </Button>
              )}
              {conversation.hasMoreRuns && runs.length > 0 && (
                <Button
                  disabled={detail.loading}
                  onClick={() => setBeforeRunId(runs[runs.length - 1].id)}
                >
                  Earlier runs
                </Button>
              )}
            </Stack>
          </Stack>
          {runs.map((run) => (
            <RunDiagnostics key={run.id} run={run} />
          ))}
          {!runs.length && (
            <Typography color="text.secondary">No runs recorded.</Typography>
          )}
        </>
      )}
    </Box>
  );
}

export default function AssistantConversations() {
  const [params] = useSearchParams();
  const id = params.get('id');
  return id ? (
    <ConversationDetail key={id} id={id} />
  ) : (
    <AssistantConversationsList />
  );
}
